import { ReactNode, useRef, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoaderOverlay from './LoaderOverlay';
import toast from 'react-hot-toast';
import { isKYCCompleted } from '../../utils/kycUtils';

interface KYCProtectedRouteProps {
  children: ReactNode;
}

const KYCProtectedRoute = ({ children }: KYCProtectedRouteProps) => {
  const { user, token, loading } = useAuth();
  const location = useLocation();
  const toastShownRef = useRef(false);

  useEffect(() => {
    if (!loading && token && user?.role === 'user' && !isKYCCompleted(user)) {
      if (location.pathname !== '/kyc' && !toastShownRef.current) {
        toastShownRef.current = true;
        toast.error(
          'KYC verification is required to access this page. Please complete your KYC verification first.'
        );
      }
    } else if (isKYCCompleted(user)) {
      toastShownRef.current = false;
    }
  }, [user, token, loading, location.pathname]);

  if (loading) {
    return <LoaderOverlay isLoading={loading} />;
  }

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.role !== 'user') {
    return <Navigate to="/unauthorized" replace />;
  }

  if (!isKYCCompleted(user)) {
    return <Navigate to="/kyc" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default KYCProtectedRoute;
